<template>
  <div class="fts-schedule-designer">
    <!-- engine switched off on this server -->
    <div class="schedule-banner schedule-banner--warn" v-if="engineOff" data-schedule-engine-off>
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <div>
        Scheduled automations are switched off on this server. You can design and save this schedule, but it
        will not run until they are switched on.
      </div>
    </div>

    <!-- how the drain sees this schedule (saved schedules only) -->
    <div class="schedule-status" v-if="routineStatus" data-schedule-status>
      <span class="schedule-status__item">
        <span class="schedule-status__label">Next run</span>
        {{ routineStatus.enabled && routineStatus.nextRunAt ? formatRunTime(routineStatus.nextRunAt) : "not scheduled" }}
      </span>
      <span class="schedule-status__item" v-if="routineStatus.lastRunAt">
        <span class="schedule-status__label">Last run</span>
        {{ formatRunTime(routineStatus.lastRunAt) }}<template v-if="routineStatus.lastStatus"> · {{ lastStatusLabel }}</template>
      </span>
      <span class="schedule-status__item">
        <span class="schedule-status__label">Runs</span>
        {{ routineStatus.runCount || 0 }}
      </span>
      <span class="schedule-status__off" v-if="!routineStatus.enabled && !originalAutomation">
        Not running: the schedule is switched off or has errors.
      </span>
    </div>
    <!-- a converted schedule waits while the automation it replaces is still on -->
    <div class="schedule-banner schedule-banner--info" v-if="originalAutomation" data-schedule-waiting>
      <font-awesome-icon :icon="['fas', 'info-circle']" />
      <div>
        This schedule replaces “{{ originalLabel }}”, which is still on and runs as before. The schedule waits, and
        takes over from its next run once that automation is switched off, so no entry is reminded twice.
      </div>
    </div>
    <div class="schedule-status schedule-status--new" v-else-if="statusLoaded">
      Not saved yet. The schedule starts running once the module is saved.
    </div>

    <!-- name -->
    <div class="design-modal-form-group">
      <label class="schedule-field-label" for="schedule-name">Name</label>
      <input
        id="schedule-name"
        class="fts-form-control"
        v-model="formData.name"
        maxlength="120"
        placeholder="e.g. Payment reminder, 3 days before due"
        data-schedule-name
      />
    </div>

    <div class="schedule-layout">
      <div class="schedule-main">
        <!-- 1 · when to run -->
        <section class="schedule-section" data-schedule-when>
          <div class="schedule-section__head">
            <span class="schedule-step">1</span>
            <span class="schedule-section__title">When to run</span>
            <span class="schedule-section__hint">{{ zoneText }}</span>
          </div>

          <div class="schedule-segmented" role="radiogroup" aria-label="How often">
            <button
              v-for="type in recurringTypes"
              :key="type.id"
              type="button"
              role="radio"
              :aria-checked="schedule.recurringType === type.id ? 'true' : 'false'"
              class="schedule-segmented__option"
              :class="{ 'is-active': schedule.recurringType === type.id }"
              @click="setRecurringType(type.id)"
            >{{ type.name }}</button>
          </div>

          <div class="schedule-row">
            <!-- daily -->
            <template v-if="schedule.recurringType === 'day'">
              <label class="schedule-inline">
                every
                <input type="number" min="1" max="365" class="fts-form-control schedule-number" v-model.number="schedule.every" />
                {{ Number(schedule.every) === 1 ? "day" : "days" }}
              </label>
              <label class="schedule-inline">
                at
                <input type="time" class="fts-form-control schedule-time" v-model="schedule.timeOfDay" data-schedule-time />
              </label>
            </template>
            <!-- hourly -->
            <template v-else-if="schedule.recurringType === 'hour'">
              <label class="schedule-inline">
                every
                <input type="number" min="1" max="23" class="fts-form-control schedule-number" v-model.number="schedule.every" />
                {{ Number(schedule.every) === 1 ? "hour" : "hours" }}
              </label>
              <label class="schedule-inline">
                at minute
                <input type="number" min="0" max="59" class="fts-form-control schedule-number" v-model.number="hourlyMinute" />
              </label>
            </template>
            <!-- weekly / monthly -->
            <label class="schedule-inline" v-else>
              at
              <input type="time" class="fts-form-control schedule-time" v-model="schedule.timeOfDay" data-schedule-time />
            </label>
          </div>

          <!-- weekly: which days -->
          <div class="schedule-chips" v-if="schedule.recurringType === 'week'">
            <button
              v-for="day in weekdays"
              :key="day.value"
              type="button"
              class="schedule-chip"
              :class="{ 'is-on': (schedule.recurringWeeks || []).indexOf(day.value) > -1 }"
              @click="toggleWeekday(day.value)"
            >{{ day.label }}</button>
          </div>

          <!-- monthly: which day -->
          <div class="schedule-checks" v-if="schedule.recurringType === 'month'">
            <label class="schedule-check">
              <input type="checkbox" v-model="schedule.recurringMonthStart" /> Start of the month
            </label>
            <label class="schedule-check">
              <input type="checkbox" v-model="monthDayChecked" /> Every
              <input type="number" min="1" max="31" class="fts-form-control schedule-number" v-model.number="monthDayEvery" />
              of the month
            </label>
            <label class="schedule-check">
              <input type="checkbox" v-model="schedule.recurringMonthEnd" /> End of the month
            </label>
          </div>

          <div class="schedule-next" data-schedule-next>
            <font-awesome-icon :icon="['fas', 'clock']" />
            <span>{{ scheduleText }}</span>
            <template v-if="nextRuns.length">
              <span class="schedule-next__sep">·</span>
              <span>Next runs: {{ nextRuns.map(formatRunTime).join(" · ") }}</span>
            </template>
          </div>
          <div class="schedule-hint">A run may start up to a minute after its time.</div>
        </section>

        <!-- 2 · find entries -->
        <section class="schedule-section" data-schedule-find>
          <div class="schedule-section__head">
            <span class="schedule-step">2</span>
            <span class="schedule-section__title">Find entries where</span>
            <span class="schedule-section__hint">the views' filter · dates count from the run day</span>
          </div>

          <fts-query-designer
            mode="schedule"
            :modules="modules"
            :states="states"
            :information-source="informationSource"
            :filter="formData.filterSchema"
            :allow-any-state="false"
            :run-day="runDay"
          />

          <div class="schedule-hint">
            <b>is on</b> 3 days from the run day picks the single day 3 days ahead, so the action comes before it;
            <b>-1</b> day is the day before the run, for a follow-up after it. <b>within</b>, <b>in the next</b> and
            <b>in the past</b> take a window. Conditions run on the server only; scripts are not offered here.
          </div>

          <div class="schedule-actions">
            <fts-btn sm outline text-transform-init :loading="previewing" @click="runPreview" data-schedule-preview>
              <font-awesome-icon :icon="['far', 'eye']" />Preview matches
            </fts-btn>
            <span class="schedule-actions__note" v-if="preview && !preview.error">
              Matches now: <b>{{ preview.matched }}</b>
            </span>
          </div>
        </section>

        <!-- 3 · then -->
        <section class="schedule-section" data-schedule-then>
          <div class="schedule-section__head">
            <span class="schedule-step">3</span>
            <span class="schedule-section__title">Then</span>
          </div>

          <div class="schedule-modes" role="radiogroup" aria-label="How the action runs">
            <label class="schedule-mode" :class="{ 'is-active': !digestMode }">
              <input type="radio" value="perEntry" v-model="mode" />
              <span>
                <span class="schedule-mode__title">For each matching entry</span>
                <span class="schedule-mode__text">
                  The action runs once per entry. Recipients, fields and blocks see that entry, exactly like a trigger.
                </span>
              </span>
            </label>
            <label class="schedule-mode" :class="{ 'is-active': digestMode }">
              <input type="radio" value="digest" v-model="mode" data-schedule-digest />
              <span>
                <span class="schedule-mode__title">Once, with all matches (digest)</span>
                <span class="schedule-mode__text">One notification listing the matches, to fixed people.</span>
              </span>
            </label>
          </div>

          <!-- per entry: which action (its editor follows below this designer) -->
          <div class="schedule-row" v-if="!digestMode">
            <span class="schedule-field-label schedule-field-label--inline">Action</span>
            <div class="schedule-segmented" role="radiogroup" aria-label="Action">
              <button
                v-for="action in actionOptions"
                :key="action.id"
                type="button"
                role="radio"
                :aria-checked="formData.actionType === action.id ? 'true' : 'false'"
                class="schedule-segmented__option"
                :class="{ 'is-active': formData.actionType === action.id }"
                @click="$emit('action-type', action.id)"
              >{{ action.name }}</button>
            </div>
            <span class="schedule-hint schedule-hint--inline">The same editor as other automations, below.</span>
          </div>

          <!-- digest -->
          <div class="schedule-digest" v-else data-schedule-digest-editor>
            <div class="design-modal-form-group">
              <span class="schedule-field-label">Send via</span>
              <div class="schedule-chips">
                <button
                  v-for="channel in channels"
                  :key="channel.value"
                  type="button"
                  class="schedule-chip"
                  :class="{ 'is-on': digestChannels.indexOf(channel.value) > -1 }"
                  @click="toggleDigestChannel(channel.value)"
                >
                  <font-awesome-icon :icon="channel.icon" v-if="channel.icon" />
                  {{ channel.name }}
                </button>
              </div>
            </div>
            <div class="design-modal-form-group">
              <span class="schedule-field-label">To</span>
              <fts-select
                :form-data="formData.digest"
                :item="{ fieldName: 'recipients' }"
                :options="userOptions"
                :reduce="(x) => x.id"
                multiple
                placeholder="Choose people"
              />
            </div>
            <div class="design-modal-form-group">
              <label class="schedule-field-label" for="schedule-digest-subject">Subject</label>
              <input id="schedule-digest-subject" class="fts-form-control" v-model="formData.digest.subject" placeholder="Defaults to the schedule's name" />
            </div>
            <div class="design-modal-form-group">
              <label class="schedule-field-label" for="schedule-digest-message">Message</label>
              <textarea
                id="schedule-digest-message"
                class="fts-form-control schedule-textarea"
                v-model="formData.digest.message"
                :placeholder="digestPlaceholder"
              ></textarea>
              <div class="schedule-chips">
                <button
                  v-for="token in digestTokens"
                  :key="token"
                  type="button"
                  class="schedule-token"
                  @click="insertDigestToken(token)"
                >+ {{ token }}</button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- delivery -->
      <aside class="schedule-delivery" data-schedule-delivery>
        <div class="schedule-delivery__title">Delivery</div>

        <label class="schedule-option" v-if="!digestMode">
          <input type="checkbox" v-model="formData.execution.oncePerEntry" />
          <span>
            <span class="schedule-option__title">Once per entry per matching day</span>
            <span class="schedule-option__text">
              If the run repeats or is retried, an entry is not acted on twice for the same date. Moving the date makes it eligible again.
            </span>
          </span>
        </label>
        <label class="schedule-option">
          <input type="checkbox" v-model="formData.execution.skipIfEmpty" />
          <span>
            <span class="schedule-option__title">Skip the run when nothing matches</span>
            <span class="schedule-option__text">No empty digests, no empty run rows.</span>
          </span>
        </label>
        <label class="schedule-option">
          <input type="checkbox" v-model="formData.execution.catchUp" />
          <span>
            <span class="schedule-option__title">Catch up a missed run</span>
            <span class="schedule-option__text">
              If the platform was down at run time, run once as soon as it is back, evaluated as of the missed day
              (up to {{ catchUpDays }} days). Off: wait for the next run.
            </span>
          </span>
        </label>
        <label class="schedule-option schedule-option--limit">
          <span class="schedule-option__title">Limit per run</span>
          <span class="schedule-inline">
            <input type="number" min="1" :max="maxPerRunCeiling" class="fts-form-control schedule-number" v-model.number="formData.execution.maxPerRun" />
            entries, then continue shortly after
          </span>
        </label>
        <div class="schedule-option__text schedule-delivery__note">
          When a run fails, workspace admins get a note. An entry that fails is retried on later runs, up to 3 times.
        </div>
        <div class="schedule-option__text schedule-delivery__note">
          Saved as one routine owned by this module: one scheduled event per run, not one per entry. Conditions are
          checked at run time, so entries created before this schedule are covered too.
        </div>

        <!-- checks -->
        <div class="schedule-problems" v-if="problems.length" data-schedule-errors>
          <div class="schedule-problems__title">
            <font-awesome-icon :icon="['fas', 'exclamation-circle']" />Fix before saving
          </div>
          <ul>
            <li v-for="problem in problems" :key="problem">{{ problem }}</li>
          </ul>
        </div>
        <div class="schedule-warnings" v-if="warnings.length" data-schedule-warnings>
          <ul>
            <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
          </ul>
        </div>

        <div class="schedule-delivery__actions">
          <fts-btn sm outline text-transform-init :loading="previewing" @click="runPreview">
            <font-awesome-icon :icon="['far', 'eye']" />Preview matches
          </fts-btn>
          <fts-btn sm outline text-transform-init :disabled="!canRunNow" :loading="running" @click="runNow" data-schedule-run-now>
            <font-awesome-icon :icon="['fas', 'play']" />Run now
          </fts-btn>
          <fts-btn sm text text-transform-init :disabled="!routineStatus" :loading="runsLoading" @click="openRuns" data-schedule-runs>
            <font-awesome-icon :icon="['fas', 'clock-rotate-left']" />Runs
          </fts-btn>
        </div>
        <div class="schedule-option__text">
          Preview sends nothing. Run now runs the saved schedule for real and is recorded.
          <template v-if="runNowBlocked"> {{ runNowBlocked }}</template>
        </div>
      </aside>
    </div>

    <!-- preview: a dialog over the designer (mockup board C) -->
    <fts-design-modal
      append-to-body
      width="880px"
      :title="preview ? `Preview · as of ${formatRunTime(preview.asOf) || 'now'}` : 'Preview'"
      desc="Nothing was sent"
      ref="previewModal"
      @closed="preview = null"
    >
    <section slot="body" class="fts-schedule-dialog" v-if="preview" data-schedule-preview-result>
      <div class="schedule-dialog__error" v-if="preview.error">
        {{ preview.error }}
        <ul v-if="preview.conditionErrors && preview.conditionErrors.length">
          <li v-for="error in preview.conditionErrors" :key="error">{{ error }}</li>
        </ul>
      </div>
      <template v-else>
        <div class="schedule-counts">
          <div class="schedule-count"><b>{{ preview.matched }}</b><span>matched</span></div>
          <template v-if="!digestMode">
            <div class="schedule-count"><b>{{ preview.wouldDeliver }}</b><span>would run</span></div>
            <div class="schedule-count"><b>{{ preview.noRecipient }}</b><span>no recipient</span></div>
            <div class="schedule-count"><b>{{ preview.alreadyDelivered }}</b><span>already done</span></div>
            <div class="schedule-count" v-if="preview.failed"><b>{{ preview.failed }}</b><span>failed</span></div>
          </template>
        </div>
        <div class="schedule-hint" v-if="preview.moreRemaining || preview.truncated">
          More entries match than one run takes; a real run continues with the rest shortly after.
        </div>
        <table class="schedule-table" v-if="!digestMode && preview.entries && preview.entries.length">
          <thead>
            <tr><th>Entry</th><th>Would do</th></tr>
          </thead>
          <tbody>
            <tr v-for="entry in preview.entries" :key="entry.entryId">
              <td>{{ entry.documentId || entry.title || `#${entry.entryId}` }}</td>
              <td>
                <span class="schedule-outcome" :class="`schedule-outcome--${entry.outcome}`">{{ outcomeLabel(entry.outcome) }}</span>
                <span v-if="entry.detail" class="schedule-muted"> · {{ entry.detail }}</span>
                <span v-if="entry.error" class="schedule-error"> · {{ entry.error }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <ul class="schedule-list" v-if="digestMode && preview.digestResults && preview.digestResults.length">
          <li v-for="(result, index) in preview.digestResults" :key="index">{{ result.detail || result.error }}</li>
        </ul>
      </template>
    </section>
    </fts-design-modal>

    <!-- runs: a dialog over the designer (mockup board C) -->
    <fts-design-modal
      append-to-body
      width="880px"
      title="Runs"
      desc="What each run did, kept 90 days"
      ref="runsModal"
      @closed="runsOpen = false"
    >
    <section slot="body" class="fts-schedule-dialog" v-if="runsOpen" data-schedule-runs-panel>
      <div class="schedule-hint" v-if="!runs.length">No runs yet.</div>
      <table class="schedule-table" v-else>
        <thead>
          <tr><th>Started</th><th>Status</th><th>Result</th><th>Took</th></tr>
        </thead>
        <tbody>
          <tr v-for="run in runs" :key="run.id">
            <td>{{ formatRunTime(run.startedAt) }}<span class="schedule-muted" v-if="run.triggeredBy === 1"> · manual</span></td>
            <td><span class="schedule-run-status" :class="`schedule-run-status--${run.status}`">{{ runStatusLabel(run.status) }}</span></td>
            <td>
              {{ runSummary(run) }}
              <span class="schedule-error" v-if="run.error"> · {{ run.error }}</span>
            </td>
            <td>{{ formatDuration(run.durationMs) }}</td>
          </tr>
        </tbody>
      </table>
    </section>
    </fts-design-modal>
  </div>
</template>

<script>
import debounce from "lodash/debounce";

import FTSBtn from "../../elements/FTSBtn";
import FTSSelect from "../../elements/FTSSelect";
import FTSDesignModal from "../FTSDesignModal";
import FTSQueryDesigner from "../FTSQueryDesigner";
import {
  CATCH_UP_DAYS,
  ENTRY_OUTCOME,
  MAX_PER_RUN_CEILING,
  RECURRING_TYPES,
  WEEKDAYS,
  defaultDigest,
  describeSchedule,
  formatDuration,
  formatRunTime,
  isDigest,
  normalizeTemplate,
  originalOf,
  relativeDateClauses,
  runStatusLabel,
  runSummary,
  scheduleApi,
  validateTemplate,
  waitsForOriginal,
} from "./scheduleLogic";
import event from "../event/event";

const DIGEST_TOKENS = Object.freeze(["{{total}}", "{{breakdown}}", "{{count}}"]);
const CHECK_TIMEOUT_MS = 8000;

/**
 * Sections 1–3 and the Delivery column of a Studio schedule (docs/scheduling-routine-automation-proposal.md
 * §2, board B). It edits the template it is given in place; the automation designer that hosts it owns
 * the per-entry action editors, Save and Cancel.
 */
export default {
  name: "FTSScheduleDesigner",
  components: {
    "fts-btn": FTSBtn,
    "fts-select": FTSSelect,
    "fts-design-modal": FTSDesignModal,
    "fts-query-designer": FTSQueryDesigner,
  },
  props: {
    formData: { type: Object, required: true },
    vectorId: { type: [Number, String], default: null },
    states: { type: Array, default: () => [] },
    informationSource: { type: Array, default: () => [] },
    modules: { type: Array, default: () => [] },
    channels: { type: Array, default: () => [] },
    beta: Boolean,
  },
  data: () => ({
    recurringTypes: RECURRING_TYPES,
    weekdays: WEEKDAYS,
    digestTokens: DIGEST_TOKENS,
    digestPlaceholder: "{{total}} item(s): {{breakdown}}",
    catchUpDays: CATCH_UP_DAYS,
    maxPerRunCeiling: MAX_PER_RUN_CEILING,
    status: null,
    statusLoaded: false,
    serverCheck: null,
    preview: null,
    previewing: false,
    running: false,
    runs: [],
    runsOpen: false,
    runsLoading: false,
  }),
  computed: {
    api() {
      return scheduleApi(this.$store.state.client);
    },
    schedule() {
      return this.formData.schedule || {};
    },
    digestMode() {
      return isDigest(this.formData);
    },
    mode: {
      get() {
        return this.digestMode ? "digest" : "perEntry";
      },
      set(value) {
        this.$set(this.formData.execution, "mode", value);
        if (value === "digest" && !this.formData.digest) this.$set(this.formData, "digest", defaultDigest());
      },
    },
    actionOptions() {
      const options = [
        { id: "notification", name: "Notification" },
        { id: "automation", name: "Automation" },
      ];
      if (this.beta || this.formData.actionType === "advanced") options.push({ id: "advanced", name: "Advanced" });
      return options;
    },
    engineOff() {
      return !!this.status && this.status.engineEnabled === false;
    },
    routineStatus() {
      if (!this.status || !this.formData.id) return null;
      return (this.status.schedules || []).find((x) => x.templateId === this.formData.id) || null;
    },
    lastStatusLabel() {
      const status = this.routineStatus && this.routineStatus.lastStatus;
      return status === "PartiallyFailed" ? "Partly failed" : status;
    },
    timeZone() {
      return (this.serverCheck && this.serverCheck.timeZone) || (this.status && this.status.timeZone) || null;
    },
    zoneText() {
      return this.timeZone ? `in the workspace's time zone, ${this.timeZone}` : "on the server's clock";
    },
    scheduleText() {
      return describeSchedule(this.schedule, null);
    },
    nextRuns() {
      return (this.serverCheck && this.serverCheck.nextRuns) || [];
    },
    /* the day relative dates resolve against in the condition rows: the next run's day */
    runDay() {
      return this.nextRuns.length ? this.nextRuns[0] : null;
    },
    hourlyMinute: {
      get() {
        const time = this.schedule.timeOfDay;
        return time && /^\d{1,2}:\d{2}$/.test(time) ? parseInt(time.split(":")[1], 10) : 0;
      },
      set(value) {
        const minute = Math.min(Math.max(parseInt(value, 10) || 0, 0), 59);
        this.$set(this.formData.schedule, "timeOfDay", `00:${String(minute).padStart(2, "0")}`);
      },
    },
    monthDayChecked: {
      get() {
        return !!this.schedule.recurringMonthDay;
      },
      set(value) {
        this.$set(this.formData.schedule, "recurringMonthDay", value);
        if (value && !(this.schedule.recurringMonthDayEvery >= 1)) this.$set(this.formData.schedule, "recurringMonthDayEvery", 1);
      },
    },
    monthDayEvery: {
      get() {
        return this.schedule.recurringMonthDayEvery || null;
      },
      set(value) {
        this.$set(this.formData.schedule, "recurringMonthDayEvery", value);
        this.$set(this.formData.schedule, "recurringMonthDay", !!value);
      },
    },
    digestChannels() {
      return (this.formData.digest && this.formData.digest.channels) || [];
    },
    userOptions() {
      return (this.$store.state.users || [])
        .filter((user) => !user.disable)
        .map((user) => {
          const username = user.userName || user.username;
          return { id: username, name: user.displayName ? `${user.displayName} (${username})` : username };
        })
        .filter((user) => user.id);
    },
    clientErrors() {
      return validateTemplate(this.formData, this.labelOf);
    },
    /* the client's checks plus what the server said about the same template */
    problems() {
      const server = (this.serverCheck && this.serverCheck.errors) || [];
      return [...new Set([...this.clientErrors, ...server])];
    },
    warnings() {
      const warnings = [];
      const dates = relativeDateClauses(this.formData.filterSchema);
      const execution = this.formData.execution || {};
      if (!this.digestMode && execution.oncePerEntry && dates.length > 1) {
        warnings.push(`There are ${dates.length} date conditions. "Once per entry" counts one delivery per date of the first one (${this.labelOf(dates[0])}).`);
      }
      if (!this.digestMode && !execution.oncePerEntry) {
        warnings.push("Without \"once per entry\", every run acts again on every entry that still matches.");
      }
      if (!this.digestMode && execution.oncePerEntry && this.schedule.recurringType === "hour") {
        warnings.push("With \"once per entry\", an hourly schedule acts on each entry at most once a day for the same date.");
      }
      return warnings;
    },
    /* the automation this schedule replaces, while it is still on (the schedule waits for it) */
    originalAutomation() {
      const triggers = this.$store.getters && this.$store.getters.vectorTriggers;
      return waitsForOriginal(this.formData, triggers) ? originalOf(this.formData, triggers) : null;
    },
    originalLabel() {
      return this.originalAutomation ? event.getEventInfo(this.originalAutomation).label : "";
    },
    runNowBlocked() {
      if (this.engineOff) return "Scheduled automations are switched off on this server.";
      if (this.originalAutomation) return "The automation this replaces is still on; switch it off first, or use Preview.";
      if (!this.routineStatus) return "Save the module first.";
      if (!this.routineStatus.enabled) return "Switch the schedule on and fix any errors first.";
      return "";
    },
    canRunNow() {
      return !this.runNowBlocked;
    },
    /* what the server validates; changes re-run the check */
    checkKey() {
      const t = this.formData;
      return JSON.stringify({
        schedule: t.schedule,
        filterSchema: t.filterSchema,
        execution: t.execution,
        digest: t.digest,
        actionType: t.actionType,
        automation: !!t.automation,
        afterScript: !!t.afterScript,
      });
    },
  },
  watch: {
    checkKey() {
      this.scheduleCheck();
    },
    /* the host opened another schedule without tearing this one down */
    formData(next, previous) {
      if (next === previous) return;
      normalizeTemplate(next, this.$set);
      this.preview = null;
      this.runs = [];
      this.runsOpen = false;
      this.serverCheck = null;
      this.refreshStatus();
      this.checkNow();
    },
  },
  created() {
    normalizeTemplate(this.formData, this.$set);
    this.scheduleCheck = debounce(this.checkNow, 400);
  },
  mounted() {
    this.refreshStatus();
    this.checkNow();
  },
  beforeDestroy() {
    this.scheduleCheck && this.scheduleCheck.cancel && this.scheduleCheck.cancel();
  },
  methods: {
    formatRunTime,
    formatDuration,
    runStatusLabel,
    runSummary,
    outcomeLabel(outcome) {
      return ENTRY_OUTCOME[outcome] || outcome;
    },
    labelOf(unit) {
      if (!unit) return "a field";
      const id = unit.fieldId || unit.field;
      const found = (this.informationSource || []).find((x) => x.id === id);
      return `"${(found && (found.label || found.displayName)) || unit.displayName || id || "a field"}"`;
    },
    setRecurringType(type) {
      this.$set(this.formData.schedule, "recurringType", type);
      if (type === "week" || type === "month") this.$set(this.formData.schedule, "every", 1);
      if (type === "hour" && !(this.schedule.every >= 1 && this.schedule.every <= 23)) this.$set(this.formData.schedule, "every", 1);
      if (type !== "hour" && /^00:\d{2}$/.test(this.schedule.timeOfDay || "")) {
        // coming back from hourly, whose time only keeps the minute
        this.$set(this.formData.schedule, "timeOfDay", "08:00");
      }
    },
    toggleWeekday(day) {
      const days = [...(this.schedule.recurringWeeks || [])];
      const index = days.indexOf(day);
      if (index > -1) days.splice(index, 1);
      else days.push(day);
      this.$set(this.formData.schedule, "recurringWeeks", WEEKDAYS.map((x) => x.value).filter((x) => days.indexOf(x) > -1));
    },
    toggleDigestChannel(channel) {
      const channels = [...this.digestChannels];
      const index = channels.indexOf(channel);
      if (index > -1) channels.splice(index, 1);
      else channels.push(channel);
      this.$set(this.formData.digest, "channels", channels);
    },
    insertDigestToken(token) {
      const message = this.formData.digest.message || "";
      this.$set(this.formData.digest, "message", `${message}${message && !/\s$/.test(message) ? " " : ""}${token}`);
    },
    async refreshStatus() {
      if (!this.vectorId) return;
      try {
        const res = await this.api.status(this.vectorId);
        this.status = res && res.data;
      } catch (err) {
        /* the status strip is informative; a failed read leaves it out */
        this.status = null;
      } finally {
        this.statusLoaded = true;
      }
    },
    async checkNow() {
      if (!this.vectorId) return null;
      const template = this.formData;
      try {
        // Save waits on this check, so a slow server must not hold the designer hostage
        const res = await Promise.race([
          this.api.validate(this.vectorId, template),
          new Promise((resolve) => setTimeout(() => resolve(null), CHECK_TIMEOUT_MS)),
        ]);
        if (template === this.formData) this.serverCheck = (res && res.data) || null;
      } catch (err) {
        if (template === this.formData) this.serverCheck = null;
      }
      return this.serverCheck;
    },
    /**
     * Called by the host designer before it writes the template into the canvas: false blocks the
     * save. The client's checks always apply; the server's apply when it answered (a failed call
     * does not block — the server keeps an invalid schedule switched off at save and at run time).
     */
    async beforeSave() {
      if (this.scheduleCheck && this.scheduleCheck.cancel) this.scheduleCheck.cancel();
      await this.checkNow();
      const problems = this.problems;
      if (problems.length) {
        this.$toasted.error(`Can't save the schedule: ${problems[0]}${problems.length > 1 ? ` (and ${problems.length - 1} more)` : ""}`);
        return false;
      }
      return true;
    },
    /* after the host saved the module: the routine row changes server-side */
    afterSave() {
      setTimeout(() => this.refreshStatus(), 1500);
    },
    openDialog(ref) {
      const dialog = this.$refs[ref];
      if (dialog && typeof dialog.show === "function") dialog.show();
    },
    async runPreview() {
      if (!this.vectorId) return;
      this.previewing = true;
      try {
        const res = await this.api.preview(this.vectorId, this.formData);
        this.preview = res && res.data;
        if (this.preview) this.openDialog("previewModal");
      } catch (err) {
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.previewing = false;
      }
    },
    async runNow() {
      if (!this.canRunNow) return;
      const confirmed = await this.$confirmation.show({
        title: "Run the schedule now?",
        text: "It runs the saved schedule for real: every matching entry is acted on, and the run is recorded. Preview matches first if you are not sure.",
      });
      if (confirmed !== true) return;

      this.running = true;
      try {
        const res = await this.api.run(this.vectorId, this.formData.id);
        const run = res && res.data;
        this.$toasted.success(run ? `Ran: ${runSummary(run)}` : "The schedule ran.");
        await this.refreshStatus();
        if (this.runsOpen) await this.loadRuns();
      } catch (err) {
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.running = false;
      }
    },
    async loadRuns() {
      this.runsLoading = true;
      try {
        const res = await this.api.runs(this.vectorId, this.formData.id);
        this.runs = (res && res.data) || [];
      } catch (err) {
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.runsLoading = false;
      }
    },
    async openRuns() {
      this.runsOpen = true;
      this.openDialog("runsModal");
      await this.loadRuns();
    },
  },
};
</script>

<style lang="scss" scoped>
.fts-schedule-designer {
  font-size: 0.875rem;
  color: var(--fts-text);

  .schedule-banner {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    margin-bottom: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.375rem;
    &.schedule-banner--warn {
      border: 1px solid rgba(var(--fts-warning-rgb, 245, 158, 11), 0.45);
      background: rgba(var(--fts-warning-rgb, 245, 158, 11), 0.08);
    }
    &.schedule-banner--info {
      border: 1px solid rgba(var(--fts-primary-rgb), 0.35);
      background: rgba(var(--fts-primary-rgb), 0.06);
    }
  }

  .schedule-status {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.25rem;
    align-items: center;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.375rem;
    background: var(--fts-gray-50);
    font-size: 0.8125rem;
    &.schedule-status--new {
      color: var(--fts-default);
    }
    .schedule-status__label {
      margin-right: 0.25rem;
      color: var(--fts-default);
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .schedule-status__off {
      color: var(--fts-danger);
    }
  }

  .schedule-field-label {
    display: block;
    margin-bottom: 0.25rem;
    color: var(--fts-default);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    &.schedule-field-label--inline {
      display: inline-block;
      margin: 0 0.5rem 0 0;
    }
  }

  .schedule-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18rem;
    gap: 1rem;
    align-items: start;
    @media (max-width: 960px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .schedule-section {
    margin-bottom: 1rem;
    padding: 0.875rem 1rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.5rem;
    background: var(--fts-white, #fff);
  }

  .schedule-section__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .schedule-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 50%;
    background: var(--fts-primary);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .schedule-section__title {
    font-weight: 600;
  }

  .schedule-section__hint,
  .schedule-hint,
  .schedule-muted {
    color: var(--fts-default);
    font-size: 0.75rem;
  }

  .schedule-hint {
    margin-top: 0.5rem;
    line-height: 1.4;
    &.schedule-hint--inline {
      margin: 0 0 0 0.5rem;
    }
  }

  .schedule-segmented {
    display: inline-flex;
    flex-wrap: wrap;
    border: 1px solid var(--fts-gray-300, #d1d5db);
    border-radius: 0.375rem;
    overflow: hidden;
    .schedule-segmented__option {
      padding: 0.3125rem 0.875rem;
      border: 0;
      border-right: 1px solid var(--fts-gray-300, #d1d5db);
      background: transparent;
      color: var(--fts-text);
      font-size: 0.8125rem;
      cursor: pointer;
      &:last-child {
        border-right: 0;
      }
      &.is-active {
        background: var(--fts-primary);
        color: #fff;
      }
    }
  }

  .schedule-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1rem;
    margin-top: 0.75rem;
  }

  .schedule-inline {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .schedule-number {
    width: 4.5rem;
  }

  .schedule-time {
    width: 8rem;
  }

  .schedule-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-top: 0.625rem;
  }

  .schedule-chip,
  .schedule-token {
    padding: 0.1875rem 0.625rem;
    border: 1px solid var(--fts-gray-300, #d1d5db);
    border-radius: 1rem;
    background: transparent;
    color: var(--fts-text);
    font-size: 0.8125rem;
    cursor: pointer;
    &.is-on {
      border-color: var(--fts-primary);
      background: rgba(var(--fts-primary-rgb), 0.1);
      color: var(--fts-primary);
    }
  }

  .schedule-token {
    border-style: dashed;
    font-size: 0.75rem;
  }

  .schedule-checks {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin-top: 0.625rem;
  }

  .schedule-check {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .schedule-next {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.75rem;
    font-size: 0.8125rem;
    .schedule-next__sep {
      color: var(--fts-default);
    }
  }

  .schedule-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .schedule-modes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 0.5rem;
  }

  .schedule-mode {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.375rem;
    cursor: pointer;
    &.is-active {
      border-color: var(--fts-primary);
      background: rgba(var(--fts-primary-rgb), 0.05);
    }
    .schedule-mode__title {
      display: block;
      font-weight: 600;
    }
    .schedule-mode__text {
      display: block;
      color: var(--fts-default);
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }

  .schedule-digest {
    margin-top: 0.75rem;
  }

  .schedule-textarea {
    min-height: 5rem;
  }

  .schedule-delivery {
    padding: 0.875rem 1rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.5rem;
    background: var(--fts-gray-50);
    .schedule-delivery__title {
      margin-bottom: 0.625rem;
      font-weight: 600;
    }
    .schedule-delivery__note {
      margin-top: 0.5rem;
    }
    .schedule-delivery__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      margin: 0.875rem 0 0.375rem;
    }
  }

  .schedule-option {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    margin-bottom: 0.625rem;
    cursor: pointer;
    &.schedule-option--limit {
      flex-direction: column;
      gap: 0.25rem;
      cursor: default;
    }
    .schedule-option__title {
      display: block;
      font-weight: 600;
      font-size: 0.8125rem;
    }
  }

  .schedule-option__text {
    display: block;
    color: var(--fts-default);
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .schedule-problems,
  .schedule-warnings {
    margin-top: 0.75rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    ul {
      margin: 0.25rem 0 0;
      padding-left: 1rem;
    }
  }

  .schedule-problems {
    border: 1px solid rgba(var(--fts-danger-rgb, 220, 38, 38), 0.35);
    background: rgba(var(--fts-danger-rgb, 220, 38, 38), 0.06);
    .schedule-problems__title {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      color: var(--fts-danger);
      font-weight: 600;
    }
  }

  .schedule-warnings {
    border: 1px solid var(--fts-gray-200);
    background: var(--fts-white, #fff);
  }
}

/* Preview and Runs dialogs: appended to the body, so they carry their own root */
.fts-schedule-dialog {
  font-size: 0.875rem;
  color: var(--fts-text);

  .schedule-dialog__error,
  .schedule-error {
    color: var(--fts-danger);
  }

  .schedule-hint,
  .schedule-muted {
    color: var(--fts-default);
    font-size: 0.75rem;
  }

  .schedule-hint {
    margin-top: 0.5rem;
    line-height: 1.4;
  }

  .schedule-counts {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-bottom: 0.5rem;
    .schedule-count {
      display: flex;
      flex-direction: column;
      b {
        font-size: 1.25rem;
      }
      span {
        color: var(--fts-default);
        font-size: 0.6875rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
    }
  }

  .schedule-table {
    width: 100%;
    margin-top: 0.5rem;
    border-collapse: collapse;
    font-size: 0.8125rem;
    th {
      padding: 0.375rem 0.5rem;
      border-bottom: 1px solid var(--fts-gray-200);
      color: var(--fts-default);
      font-size: 0.6875rem;
      font-weight: 600;
      text-align: left;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    td {
      padding: 0.375rem 0.5rem;
      border-bottom: 1px solid var(--fts-gray-100);
      vertical-align: top;
    }
  }

  .schedule-list {
    margin: 0.5rem 0 0;
    padding-left: 1rem;
  }

  .schedule-outcome--3,
  .schedule-outcome--4,
  .schedule-run-status--2,
  .schedule-run-status--3 {
    color: var(--fts-danger);
  }

  .schedule-outcome--0,
  .schedule-outcome--1,
  .schedule-run-status--1 {
    color: var(--fts-success);
  }
}
</style>
